import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch";
import Swal from "sweetalert2";
import { createFunctionalityIndicatorsService } from "../services/createFunctionalityIndicators.service";
import { updateFunctionalityIndicatorsService } from "../services/updateFunctionalityIndicators.service";
import { deleteFunctionalityIndicatorsService } from "../services/deleteFunctionalityIndicators.service";

const initialState = {
  id: null,
  companyId: null,
  date: "",
  dailyWorkShiftExcesses: "",
  totalDriverWorkDays: "",
  dailySpeedingWorkTrips: "",
  totalWorkTrips: "",
};

export const useFunctionalityIndicatorsCommand = () => {
  const fetchData = useFetch();
  const [loading, setLoading] = useState(false);
  const [indicators, setIndicators] = useState(initialState);
  const [errors, setErrors] = useState({ hasErrors: false, errors: {} });

  const handleChange = ({ name, value }) => {
    setIndicators((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      hasErrors: false,
      errors: {
        ...prev.errors,
        [name]: "",
      },
    }));
  };

  const loadIndicator = (data) => {
    setIndicators({
      id: data.id,
      companyId: data.companyId,
      date: data.date || "",
      dailyWorkShiftExcesses: data.dailyWorkShiftExcesses ?? "",
      totalDriverWorkDays: data.totalDriverWorkDays ?? "",
      dailySpeedingWorkTrips: data.dailySpeedingWorkTrips ?? "",
      totalWorkTrips: data.totalWorkTrips ?? "",
    });
  };

  const createIndicator = async (data) => {
    try {
      setLoading(true);
      if (validations(data)) {
        return;
      }
      const service = createFunctionalityIndicatorsService(fetchData);
      const response = await service(data);
      setIndicators(initialState);
      setErrors({ hasErrors: false, errors: {} });
      Swal.fire({
        title: "Éxito",
        text: "Indicador creado correctamente",
        icon: "success",
      });
      return response;
    } catch (error) {
      console.error("Error creating functionality indicator:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateIndicator = async (data) => {
    try {
      setLoading(true);
      if (validations(data)) {
        return;
      }
      const service = updateFunctionalityIndicatorsService(fetchData);
      const response = await service(data.id, data);
      Swal.fire({
        title: "Éxito",
        text: "Indicador actualizado correctamente",
        icon: "success",
      });
      return response;
    } catch (error) {
      console.error("Error updating functionality indicator:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteIndicator = async (id) => {
    try {
      const confirm = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return false;

      setLoading(true);
      const service = deleteFunctionalityIndicatorsService(fetchData);
      await service(id);
      Swal.fire({
        title: "Eliminado",
        text: "El indicador ha sido eliminado correctamente",
        icon: "success",
      });
      return true;
    } catch (error) {
      console.error("Error deleting functionality indicator:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar el indicador",
        icon: "error",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const validations = (data) => {
    const newErrors = {};

    if (!data.date) {
      newErrors.date = "La fecha es obligatoria";
    }

    if (data.type === "mensual") {
      if (!data.totalDriverWorkDays) {
        newErrors.totalDriverWorkDays =
          "El total de días trabajados por conductor es obligatorio";
      }
      if (!data.totalWorkTrips) {
        newErrors.totalWorkTrips =
          "El total de viajes de trabajo es obligatorio";
      }
    } else {
      if (!data.dailyWorkShiftExcesses) {
        newErrors.dailyWorkShiftExcesses =
          "El exceso de turnos diarios es obligatorio";
      }
      if (!data.dailySpeedingWorkTrips) {
        newErrors.dailySpeedingWorkTrips =
          "Los viajes diarios con exceso de velocidad son obligatorios";
      }
    }

    const hasErrors = Object.keys(newErrors).length > 0;
    setErrors({ hasErrors, errors: newErrors });
    return hasErrors;
  };

  return {
    indicators,
    loading,
    handleChange,
    loadIndicator,
    createIndicator,
    updateIndicator,
    deleteIndicator,
    validations,
    errors,
  };
};
